import {
  Component,
  EventEmitter,
  Input,
  Output,
  forwardRef,
  OnInit,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  ɵInternalFormsSharedModule,
  FormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-dropdown',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatIconModule,
    ɵInternalFormsSharedModule,
  ],
  templateUrl: './dropdown.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownComponent),
      multi: true,
    },
  ],
  styleUrl: './dropdown.component.scss',
})
export class DropdownComponent implements ControlValueAccessor, OnInit {
  ngOnInit() {
    if (!this.compareWith && this.valueKey) {
      this.compareWith = (a: any, b: any) => {
        if (a == null || b == null) return a === b;
        // Compare by valueKey if both are objects
        if (
          typeof a === 'object' &&
          typeof b === 'object' &&
          a[this.valueKey] !== undefined &&
          b[this.valueKey] !== undefined
        ) {
          return a[this.valueKey] === b[this.valueKey];
        }
        // Compare object to primitive
        if (typeof a === 'object' && a[this.valueKey] !== undefined) {
          return a[this.valueKey] === b;
        }
        if (typeof b === 'object' && b[this.valueKey] !== undefined) {
          return a === b[this.valueKey];
        }
        return a === b;
      };
    } else {
      this.compareWith ??= (a: any, b: any) => a === b;
    }
  }
  @Input() label: string = '';
  @Input() options: any[] = [];
  @Input() multiSelect: boolean = false;
  @Input() showSelectAll: boolean = false;
  @Input() showFilter: boolean = false;
  @Input() idSelect: string = 'single_select';
  @Input() outlined: boolean = true;
  @Input() valueKey: string = '';
  @Input() displayKey: string = '';
  @Input() iconKey: string = '';
  @Input() preSelectedLabel: string = '';
  @Input() preSelectedValue: string = '';
  @Input() optionsValueKey: string = '';
  @Input() optionsLabelKey: string = '';
  @Input() compareWith: (o1: any, o2: any) => boolean = (a, b) => a === b;
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;

  private onChangeFn: (val: any) => void = () => {};
  private onTouchedFn: () => void = () => {};
  public _value: any = null;
  @Output() selectionChange = new EventEmitter<any>();
  @Output() dropdownChange = new EventEmitter<any>();

  constructor() {}
  writeValue(val: any): void {
    // If compareWith is provided and options are available, find the matching option
    if (val && this.compareWith && Array.isArray(this.options)) {
      const match = this.options.find((opt) => this.compareWith(opt, val));
      this._value = match || val;
    } else {
      this._value = val;
    }
  }
  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onDataChange(value: any): void {
    this._value = value;
    this.onChangeFn(value);
    this.onTouchedFn();
    this.selectionChange.emit(value);
    this.dropdownChange.emit(value);
  }
}
